import React, { useRef, useState } from 'react';
import { IconButton, ListItem, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PreviewIcon from '@mui/icons-material/Preview';
import { useFieldApi } from '@truvity/widget__forms';

import { Credentials, LinkedCredentialsWidget } from '../../../../widgets/linkedCredentials';
import { CredentialPreview } from '../CredentialPreview';

export type CredentialSuggestProps = {
    id: string;
    name: string;
    schemaIds: string[];
    onSelect: (value: string) => void;
    isDisabled?: boolean;
};

export const CredentialSuggest = ({ id, name, schemaIds, onSelect, isDisabled }: CredentialSuggestProps) => {
    const { getWidgetApi } = useFieldApi();
    const {
        suggestCredentials,
        options: { walletDid, entityDid },
    } = getWidgetApi(LinkedCredentialsWidget);

    const menuAnchor = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [suggestedResults, setSuggestedResults] = useState<Credentials>([]);

    // HANDLERS
    const handleShowSuggestions = async () => {
        setIsLoading(true);

        try {
            const results = await suggestCredentials(schemaIds);
            setSuggestedResults(results);
            setIsOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseSuggestions = () => setIsOpen(false);

    const handleSelectSuggestion = (value: string) => {
        handleCloseSuggestions();
        onSelect(value);
    };

    // RENDER
    const renderSuggestedResults = () => {
        const menuItemWidth = menuAnchor.current?.clientWidth;

        if (suggestedResults.length === 0) {
            return (
                <ListItem sx={{ width: menuItemWidth }}>
                    <ListItemText primary="No suitable credentials to use" />
                </ListItem>
            );
        }

        return suggestedResults.map((item) => (
            <MenuItem key={item.VcDid} sx={{ width: menuItemWidth }} onClick={() => handleSelectSuggestion(item.VcDid)}>
                <CredentialPreview credentialId={item.VcDid} walletDid={walletDid} entityDid={entityDid}>
                    {({ showPreview }) => (
                        <ListItemIcon>
                            <IconButton
                                edge="start"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    showPreview();
                                }}
                            >
                                <PreviewIcon />
                            </IconButton>
                        </ListItemIcon>
                    )}
                </CredentialPreview>
                <ListItemText primary={item.Preview} />
            </MenuItem>
        ));
    };

    return (
        <>
            <LoadingButton
                ref={menuAnchor}
                id={id}
                name={name}
                variant="outlined"
                loading={isLoading}
                onClick={handleShowSuggestions}
                sx={{
                    height: 72, // 72px to match `CredentialCard`
                }}
                fullWidth
                disabled={isDisabled}
            >
                Select documents
            </LoadingButton>
            <Menu
                id={`${id}-menu`}
                anchorEl={menuAnchor.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={isOpen}
                onClose={handleCloseSuggestions}
                MenuListProps={{
                    'aria-labelledby': id,
                }}
            >
                {renderSuggestedResults()}
            </Menu>
        </>
    );
};
